export interface IReaderContentsProgressParagraphMetric {
    top: number,
    bottom: number,
    height: number,
}

interface ResolveReaderContentsProgressArgs {
    currentPostParagraphCount: number,
    renderedParagraphMetrics: IReaderContentsProgressParagraphMetric[],
    viewportTop: number,
    viewportBottom: number,
    hasReachedPostEnd: boolean,
    hasRenderedWholePost: boolean,
}

const clamp = (value: number, min: number, max: number): number => (
    Math.min(max, Math.max(min, value))
);

/**
 * Resolves contents-table reading progress from the currently rendered
 * paragraphs in the active reader scope.
 *
 * The reader intentionally anchors progress to the first visible paragraph so
 * the bar rises smoothly instead of jumping ahead to the last paragraph in
 * view. When the scroll surface has reached the bottom of the post and every
 * known paragraph is rendered, the result is clamped to 100% so short final
 * viewports do not leave the bar slightly incomplete.
 *
 * @param currentPostParagraphCount total paragraph count in the active scope
 * @param renderedParagraphMetrics ordered DOM metrics for rendered paragraphs
 * @param viewportTop top edge of the visible viewport for the reader surface
 * @param viewportBottom bottom edge of the visible viewport for the reader surface
 * @param hasReachedPostEnd indicates whether the reader is scrolled to the end
 * @param hasRenderedWholePost indicates whether the active reader scope finished rendering
 * @returns progress percentage from 0 to 100, or null when no visible anchor exists
 */
export default function resolveReaderContentsProgress({
    currentPostParagraphCount,
    renderedParagraphMetrics,
    viewportTop,
    viewportBottom,
    hasReachedPostEnd,
    hasRenderedWholePost,
}: ResolveReaderContentsProgressArgs): number | null {
    if (
        currentPostParagraphCount <= 0
        || renderedParagraphMetrics.length === 0
    ) {
        return null;
    }

    if (
        hasReachedPostEnd
        && hasRenderedWholePost
    ) {
        return 100;
    }

    const activeParagraphIndex = renderedParagraphMetrics.findIndex((paragraphMetric) => {
        const overlapTop = Math.max(paragraphMetric.top, viewportTop);
        const overlapBottom = Math.min(paragraphMetric.bottom, viewportBottom);
        const overlapHeight = Math.max(0, overlapBottom - overlapTop);

        return overlapHeight > 0;
    });

    if (activeParagraphIndex < 0) {
        return null;
    }

    const activeParagraphMetric = renderedParagraphMetrics[activeParagraphIndex];
    const paragraphHeight = Math.max(
        activeParagraphMetric.height,
        activeParagraphMetric.bottom - activeParagraphMetric.top,
        1,
    );
    const activeParagraphRatio = clamp(
        (viewportTop - activeParagraphMetric.top) / paragraphHeight,
        0,
        1,
    );

    return 100 * clamp(
        (activeParagraphIndex + activeParagraphRatio) / Math.max(currentPostParagraphCount, 1),
        0,
        1,
    );
}
